import { ImagePlus, Loader2, MonitorPlay, Newspaper, Plus, Save, Trash2, Upload, Video, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { adminApi } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type { SiteNewsItem, SiteVideoBand } from "../../../types/api";
import { fallbackNews } from "../../aan-site/newsData";

const defaultVideoBand: SiteVideoBand = {
  active: true,
  title: "African Art Network video",
  videoUrl: "",
  posterImage: "",
  embedUrl:
    "https://player.vimeo.com/video/1104958818?autoplay=1&playsinline=1&loop=1&muted=1&title=0&portrait=0&byline=0",
};

function createNewsDraft(): SiteNewsItem {
  return {
    title: "",
    slug: "",
    excerpt: "",
    body: "",
    image: "",
    imageAlt: "",
    author: "African Art Network",
    publishedAt: new Date().toISOString().slice(0, 10),
    published: true,
  };
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AdminSiteContentPage() {
  const { admin, token } = useAdminAuth();
  const { showToast } = useToast();
  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const posterInputRef = useRef<HTMLInputElement | null>(null);
  const newsImageInputRef = useRef<HTMLInputElement | null>(null);
  const newsUploadIndexRef = useRef<number | null>(null);
  const [videoForm, setVideoForm] = useState<SiteVideoBand>(defaultVideoBand);
  const [newsItems, setNewsItems] = useState<SiteNewsItem[]>(fallbackNews);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [isUploadingPoster, setIsUploadingPoster] = useState(false);
  const [uploadingNewsImageIndex, setUploadingNewsImageIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canManage = admin?.role === "super-admin" || admin?.effectivePermissions?.includes("site:manage");

  useEffect(() => {
    if (!token) return;
    let active = true;

    setIsLoading(true);
    setError(null);
    adminApi
      .getSiteContent(token)
      .then(({ content }) => {
        if (active) {
          setVideoForm(content.videoBand);
          setNewsItems(content.news);
        }
      })
      .catch((requestError) => {
        const message = requestError instanceof Error ? requestError.message : "Could not load site content";
        if (active) setError(message);
        showToast(message, "error");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [showToast, token]);

  const uploadVideo = async (file?: File) => {
    if (!token || !file) return;
    if (!file.type.startsWith("video/")) {
      showToast("Please select a video file", "error");
      return;
    }

    setIsUploadingVideo(true);
    try {
      const { url } = await adminApi.uploadVideo(token, file);
      setVideoForm((current) => ({ ...current, videoUrl: url }));
      showToast("Video uploaded", "success");
    } catch (uploadError) {
      showToast(uploadError instanceof Error ? uploadError.message : "Could not upload video", "error");
    } finally {
      setIsUploadingVideo(false);
    }
  };

  const uploadPoster = async (file?: File) => {
    if (!token || !file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file", "error");
      return;
    }

    setIsUploadingPoster(true);
    try {
      const { url } = await adminApi.uploadImage(token, file);
      setVideoForm((current) => ({ ...current, posterImage: url }));
      showToast("Poster uploaded", "success");
    } catch (uploadError) {
      showToast(uploadError instanceof Error ? uploadError.message : "Could not upload poster", "error");
    } finally {
      setIsUploadingPoster(false);
    }
  };

  const updateNewsItem = (index: number, updates: Partial<SiteNewsItem>) => {
    setNewsItems((current) => current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...updates } : item)));
  };

  const addNewsItem = () => {
    setNewsItems((current) => [createNewsDraft(), ...current]);
  };

  const deleteNewsItem = (index: number) => {
    const item = newsItems[index];
    const confirmed = window.confirm(`Delete "${item?.title || "this news item"}"?`);
    if (!confirmed) return;
    setNewsItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const selectNewsImage = (index: number) => {
    newsUploadIndexRef.current = index;
    setUploadingNewsImageIndex(index);
    newsImageInputRef.current?.click();
  };

  const uploadNewsImage = async (file?: File) => {
    const itemIndex = newsUploadIndexRef.current;
    if (!token || !file || itemIndex === null) {
      setUploadingNewsImageIndex(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file", "error");
      setUploadingNewsImageIndex(null);
      return;
    }

    setUploadingNewsImageIndex(itemIndex);
    try {
      const { url } = await adminApi.uploadImage(token, file);
      setNewsItems((current) =>
        current.map((item, index) =>
          index === itemIndex ? { ...item, image: url, imageAlt: item.imageAlt || item.title } : item
        )
      );
      showToast("News image uploaded", "success");
    } catch (uploadError) {
      showToast(uploadError instanceof Error ? uploadError.message : "Could not upload news image", "error");
    } finally {
      newsUploadIndexRef.current = null;
      setUploadingNewsImageIndex(null);
    }
  };

  const prepareNewsForSave = () => {
    const usedSlugs = new Set<string>();

    return newsItems.map((item, index) => {
      const title = item.title.trim();
      if (!title) {
        throw new Error(`News item ${index + 1} needs a title before saving`);
      }

      const baseSlug = slugify(item.slug || title) || `news-${index + 1}`;
      let slug = baseSlug;
      let suffix = 2;
      while (usedSlugs.has(slug)) {
        slug = `${baseSlug}-${suffix}`;
        suffix += 1;
      }
      usedSlugs.add(slug);

      return {
        ...item,
        title,
        slug,
        excerpt: item.excerpt.trim(),
        body: item.body.trim(),
        image: item.image.trim(),
        imageAlt: item.imageAlt.trim() || title,
        author: item.author.trim(),
        publishedAt: item.publishedAt.trim(),
      };
    });
  };

  const saveContent = async () => {
    if (!token) return;

    setIsSaving(true);
    setError(null);
    try {
      const news = prepareNewsForSave();
      const { content } = await adminApi.updateSiteContent(token, {
        videoBand: {
          ...videoForm,
          title: videoForm.title.trim() || defaultVideoBand.title,
          videoUrl: videoForm.videoUrl.trim(),
          posterImage: videoForm.posterImage.trim(),
          embedUrl: videoForm.embedUrl.trim(),
        },
        news,
      });
      setVideoForm(content.videoBand);
      setNewsItems(content.news);
      showToast("Site content updated", "success");
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Could not save site content";
      setError(message);
      showToast(message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="ego-surface px-6 py-12 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#fef2f2] text-[#dc2626]">
          <MonitorPlay size={20} />
        </div>
        <h2 className="text-lg font-bold text-foreground">Site content is restricted</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Only super admins and roles with site content access can update public website media.
        </p>
      </div>
    );
  }

  if (isLoading) return <LoadingState label="Loading site content..." />;

  return (
    <div className="space-y-6">
      {error && (
        <div className="ego-surface border-l-4 border-l-red-500 px-5 py-4 text-sm font-semibold text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
        <section className="ego-surface overflow-hidden">
          <PanelHeader
            icon={MonitorPlay}
            title="Homepage video section"
            subtitle="Upload the media used in the large public AAN website video band."
          />

          <div className="space-y-5 p-5">
            <label className="flex items-center gap-3 text-sm font-semibold text-foreground">
              <input
                type="checkbox"
                checked={videoForm.active}
                onChange={(event) => setVideoForm((current) => ({ ...current, active: event.target.checked }))}
                className="h-4 w-4 rounded border-line text-[var(--pp-gold)] focus:ring-[var(--pp-gold)]"
              />
              Show this section on the public site
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-foreground">Video title</span>
              <input
                value={videoForm.title}
                onChange={(event) => setVideoForm((current) => ({ ...current, title: event.target.value }))}
                className="ego-input"
                placeholder="African Art Network video"
              />
            </label>

            <div className="rounded-[8px] border border-line bg-surface-muted p-4">
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/mpeg,video/quicktime,video/webm,video/x-m4v"
                className="hidden"
                onChange={(event) => {
                  void uploadVideo(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-bold text-foreground">Uploaded video</div>
                  <div className="mt-1 max-w-lg truncate text-xs text-muted-foreground">
                    {videoForm.videoUrl || "No uploaded video selected"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={isUploadingVideo}
                    className="ego-btn-primary"
                  >
                    {isUploadingVideo ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {videoForm.videoUrl ? "Replace" : "Upload"}
                  </button>
                  {videoForm.videoUrl && (
                    <button
                      type="button"
                      onClick={() => setVideoForm((current) => ({ ...current, videoUrl: "" }))}
                      className="ego-btn-secondary"
                    >
                      <X size={16} /> Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-foreground">Uploaded video URL</span>
              <input
                value={videoForm.videoUrl}
                onChange={(event) => setVideoForm((current) => ({ ...current, videoUrl: event.target.value }))}
                className="ego-input"
                placeholder="Upload a video or paste a video file URL"
              />
            </label>

            <div className="rounded-[8px] border border-line bg-surface-muted p-4">
              <input
                ref={posterInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  void uploadPoster(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-bold text-foreground">Poster image</div>
                  <div className="mt-1 max-w-lg truncate text-xs text-muted-foreground">
                    {videoForm.posterImage || "Optional fallback image for uploaded video"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => posterInputRef.current?.click()}
                    disabled={isUploadingPoster}
                    className="ego-btn-secondary"
                  >
                    {isUploadingPoster ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                    {videoForm.posterImage ? "Replace" : "Upload"}
                  </button>
                  {videoForm.posterImage && (
                    <button
                      type="button"
                      onClick={() => setVideoForm((current) => ({ ...current, posterImage: "" }))}
                      className="ego-btn-secondary"
                    >
                      <X size={16} /> Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-foreground">External embed URL</span>
              <input
                value={videoForm.embedUrl}
                onChange={(event) => setVideoForm((current) => ({ ...current, embedUrl: event.target.value }))}
                className="ego-input"
                placeholder="Fallback Vimeo or external player URL"
              />
            </label>

            <button type="button" onClick={saveContent} disabled={isSaving} className="ego-btn-primary w-full">
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save site content
            </button>
          </div>
        </section>

        <section className="ego-surface overflow-hidden">
          <PanelHeader
            icon={Video}
            title="Preview"
            subtitle="Uploaded video takes priority over the external embed URL."
          />
          <div className="p-5">
            <div className="relative aspect-video overflow-hidden rounded-[8px] bg-black">
              {videoForm.videoUrl ? (
                <video
                  controls
                  className="h-full w-full object-contain"
                  poster={videoForm.posterImage || undefined}
                  src={videoForm.videoUrl}
                />
              ) : videoForm.embedUrl ? (
                <iframe
                  allow="autoplay; fullscreen; picture-in-picture"
                  className="h-full w-full border-0"
                  src={videoForm.embedUrl}
                  title={videoForm.title || defaultVideoBand.title}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm font-semibold text-white/60">
                  Add a video upload or embed URL
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <section className="ego-surface overflow-hidden">
        <PanelHeader
          icon={Newspaper}
          title="News articles"
          subtitle="Create, update, publish, unpublish, and delete the news shown on the public website."
        />

        <div className="space-y-5 p-5">
          <input
            ref={newsImageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(event) => {
              void uploadNewsImage(event.target.files?.[0]);
              event.target.value = "";
            }}
          />

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-bold text-foreground">Homepage and detail-page news</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Published items appear on the homepage and open at /news/article-slug.
              </p>
            </div>
            <button type="button" onClick={addNewsItem} className="ego-btn-primary">
              <Plus size={16} />
              New news
            </button>
          </div>

          {newsItems.length === 0 ? (
            <div className="rounded-[8px] border border-dashed border-line bg-surface-muted px-6 py-12 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-[8px] bg-aan-green text-black">
                <Newspaper size={20} />
              </div>
              <h3 className="text-lg font-bold text-foreground">No news articles yet</h3>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                Add a news article to publish it on the public AAN website.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {newsItems.map((item, index) => {
                const publicSlug = slugify(item.slug || item.title) || `news-${index + 1}`;
                return (
                  <article
                    key={`${item.slug || "news"}-${index}`}
                    className="rounded-[8px] border border-line bg-surface-muted p-4"
                  >
                    <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
                      <div className="space-y-3">
                        <div className="overflow-hidden rounded-[8px] border border-line bg-black">
                          {item.image ? (
                            <img
                              className="aspect-[4/3] w-full object-cover"
                              src={item.image}
                              alt={item.imageAlt || item.title || "News image"}
                            />
                          ) : (
                            <div className="flex aspect-[4/3] items-center justify-center px-5 text-center text-xs font-semibold text-white/55">
                              No image selected
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => selectNewsImage(index)}
                            disabled={uploadingNewsImageIndex === index}
                            className="ego-btn-secondary flex-1 !py-2 text-xs"
                          >
                            {uploadingNewsImageIndex === index ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <ImagePlus size={14} />
                            )}
                            {item.image ? "Replace" : "Upload"}
                          </button>
                          {item.image && (
                            <button
                              type="button"
                              onClick={() => updateNewsItem(index, { image: "" })}
                              className="ego-btn-secondary !py-2 !px-3 text-xs"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <label className="flex items-center gap-3 text-sm font-semibold text-foreground">
                            <input
                              type="checkbox"
                              checked={item.published}
                              onChange={(event) => updateNewsItem(index, { published: event.target.checked })}
                              className="h-4 w-4 rounded border-line text-[var(--pp-gold)] focus:ring-[var(--pp-gold)]"
                            />
                            Published
                          </label>
                          <button
                            type="button"
                            onClick={() => deleteNewsItem(index)}
                            className="ego-btn-danger !py-2 text-xs"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="block">
                            <span className="mb-1.5 block text-sm font-semibold text-foreground">Title</span>
                            <input
                              value={item.title}
                              onChange={(event) => updateNewsItem(index, { title: event.target.value })}
                              className="ego-input"
                              placeholder="News headline"
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1.5 block text-sm font-semibold text-foreground">Slug</span>
                            <input
                              value={item.slug}
                              onChange={(event) => updateNewsItem(index, { slug: event.target.value })}
                              className="ego-input"
                              placeholder="news-url-slug"
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1.5 block text-sm font-semibold text-foreground">Author</span>
                            <input
                              value={item.author}
                              onChange={(event) => updateNewsItem(index, { author: event.target.value })}
                              className="ego-input"
                              placeholder="African Art Network"
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1.5 block text-sm font-semibold text-foreground">Publish date</span>
                            <input
                              type="date"
                              value={item.publishedAt}
                              onChange={(event) => updateNewsItem(index, { publishedAt: event.target.value })}
                              className="ego-input"
                            />
                          </label>
                        </div>

                        <label className="block">
                          <span className="mb-1.5 block text-sm font-semibold text-foreground">Excerpt</span>
                          <textarea
                            value={item.excerpt}
                            onChange={(event) => updateNewsItem(index, { excerpt: event.target.value })}
                            className="ego-input min-h-24 resize-y"
                            placeholder="Short homepage summary"
                          />
                        </label>

                        <label className="block">
                          <span className="mb-1.5 block text-sm font-semibold text-foreground">Article body</span>
                          <textarea
                            value={item.body}
                            onChange={(event) => updateNewsItem(index, { body: event.target.value })}
                            className="ego-input min-h-40 resize-y"
                            placeholder="Full detail-page article"
                          />
                        </label>

                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="block">
                            <span className="mb-1.5 block text-sm font-semibold text-foreground">Image URL</span>
                            <input
                              value={item.image}
                              onChange={(event) => updateNewsItem(index, { image: event.target.value })}
                              className="ego-input"
                              placeholder="Upload or paste an image URL"
                            />
                          </label>
                          <label className="block">
                            <span className="mb-1.5 block text-sm font-semibold text-foreground">Image alt text</span>
                            <input
                              value={item.imageAlt}
                              onChange={(event) => updateNewsItem(index, { imageAlt: event.target.value })}
                              className="ego-input"
                              placeholder="Describe the image"
                            />
                          </label>
                        </div>

                        <div className="rounded-[8px] border border-line bg-black/30 px-4 py-3 text-xs font-semibold text-muted-foreground">
                          Public URL: /news/{publicSlug}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <button type="button" onClick={saveContent} disabled={isSaving} className="ego-btn-primary w-full">
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save site content
          </button>
        </div>
      </section>
    </div>
  );
}

function PanelHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: typeof MonitorPlay;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-start gap-3 border-b border-line px-5 py-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-aan-green text-black">
        <Icon size={18} />
      </div>
      <div>
        <h2 className="text-lg font-bold text-foreground">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
